import { useState, useEffect, useRef } from 'react';
import { projectService } from '../services/projectService';
import { authService } from '../services/authService';
import { canEditAtLevel } from '@shared-lib/org-access';

/**
 * Hook to check user's access level for a project
 * Returns access level and loading state
 *
 * @param {string} projectId - The project ID to check access for
 * @param {string} structureId - Optional structure ID to find the project
 * @returns {Object} { accessLevel: 'owner'|'editor'|'viewer'|'none', isViewOnly: boolean, loading: boolean }
 *   `isViewOnly` is the inverse of `canEdit`. Only 'owner' and 'editor' (an
 *   owner / office member of the organization that owns the project,
 *   `@shared-lib/org-access`) may edit; every other state is read-only, so an
 *   unresolved or failed access check fails closed.
 *
 * `loading` describes the FIRST resolution and nothing after it. It used to be
 * set back to true at the top of every re-check, with an `await` between that
 * and the matching `false` — so React committed a render with `loading: true`,
 * and `StructureViewerWrapper`'s loading gate unmounts the entire 3D tree while
 * that is set. One re-check therefore destroyed the WebGL context, discarded
 * every loaded GLB, and spent one of the page's ~16 contexts
 * (reference-docs/webview-resilience/). A re-check now updates `accessLevel` in
 * place instead.
 */
export function useProjectAccess(projectId, structureId) {
  const [accessLevel, setAccessLevel] = useState('none');
  const [loading, setLoading] = useState(true);
  const [resolvedProjectId, setResolvedProjectId] = useState(projectId);
  // The owner's uid, straight off the project doc this hook already fetches —
  // so admin chrome can name the owner without a second read of `projects`.
  const [ownerId, setOwnerId] = useState(null);

  // Auth as a SUBSCRIPTION, not a render-time read. `authService.getCurrentUser()`
  // called during render is not reactive — nothing re-runs when the session
  // restores — and it is re-read on every render, so a transient null makes the
  // effect's dependency flip and re-fire for a user who never changed.
  const [uid, setUid] = useState(
    () => authService.getCurrentUser()?.uid ?? null
  );

  useEffect(
    () => authService.onAuthStateChanged((user) => setUid(user?.uid ?? null)),
    []
  );

  // Which (project, structure, user) the held `accessLevel` is an answer FOR.
  // Null until the first resolution.
  const resolvedForRef = useRef(null);

  useEffect(() => {
    const subject = `${projectId || ''}|${structureId || ''}|${uid || ''}`;
    let cancelled = false;

    // A re-check for a DIFFERENT subject must not keep answering with the old
    // project's level — `canEdit` gates every mutation handler. Fail closed
    // while the new answer is in flight. (A re-check for the same subject
    // cannot reach here: every dependency below is a primitive.)
    if (resolvedForRef.current !== null && resolvedForRef.current !== subject) {
      setAccessLevel('none');
      // Belongs to the project we are leaving, not the one being resolved.
      setOwnerId(null);
    }

    const settle = (level) => {
      if (cancelled) return;
      setAccessLevel(level);
      resolvedForRef.current = subject;
      setLoading(false);
    };

    const checkAccess = async () => {
      if (!uid) {
        settle('none');
        return;
      }

      try {
        let actualProjectId = projectId;

        // If no projectId but we have a structureId, find the project
        if (!actualProjectId && structureId) {
          const project =
            await projectService.findProjectByStructureId(structureId);
          if (cancelled) return;
          if (!project) {
            settle('none');
            return;
          }
          actualProjectId = project.id;
          setResolvedProjectId(actualProjectId);
        }

        // If still no project ID, assume no access
        if (!actualProjectId) {
          settle('none');
          return;
        }

        // Fetch project from Firestore to get fresh access data. The token's
        // claims say which organization the user edits for — the same token
        // the rules will judge the edits by. The two are read together, but
        // the project settles on its own: a claims read that fails (an
        // offline refresh of an expired token) must not discard a project
        // that was fetched, so the admin chrome can still name its owner,
        // and it fails CLOSED to no organization rather than to no access.
        const [project, claims] = await Promise.all([
          projectService.getProject(actualProjectId),
          Promise.resolve()
            .then(() => authService.getTokenClaims())
            .catch((error) => {
              console.error('Could not read the token claims:', error);
              return {};
            }),
        ]);
        if (cancelled) return;

        setOwnerId(project?.ownerId || null);
        settle(projectService.getUserAccessLevel(project, uid, claims));
      } catch (error) {
        if (cancelled) return;
        console.error('Error checking project access:', error);
        // If we can't fetch the project, assume no access
        settle('none');
      }
    };

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, [projectId, structureId, uid]);

  const isOwner = accessLevel === 'owner';
  const canEdit = canEditAtLevel(accessLevel);

  return {
    accessLevel,
    // Read-only is "may you edit?", NOT "are you a viewer?". Of the states
    // that reach this only 'owner' and 'editor' may write: a shared viewer,
    // a non-member (an admin opening a project nobody shared with them), and
    // the catch above that reports 'none' when the project fetch fails all
    // may not. Keying on 'viewer' let the last two through with every
    // mutation handler wired, so a transient Firestore error turned a
    // viewer's page editable. Admins are deliberately not an exception —
    // they copy a project to change it (`copySharedProject`).
    isViewOnly: !canEdit,
    // The project's `ownerId` only. An organization editor can change the
    // project's contents but, like the rules, not delete it or re-home it.
    isOwner,
    canEdit,
    loading,
    ownerId,
    resolvedProjectId,
  };
}
